import {memoize, once} from '@packages/utilities/memo';
import {getExpandedBindingSources} from '../binding-sources';
import {getDataType} from './getDataType';
import {
  areReferencesToSameEntity,
  EntityBindingContext,
  ListBindingContext,
  PageBindingContext,
} from '../../primitives';
import type {
  BindingSource,
  BindingContext,
  BindingContextType,
  BindingContextTypeReference,
} from '../../primitives';
import type {Gateways, Gateway} from '../../types';
import {
  type PluginName,
  pluginNameToString,
} from '@packages/systems/wfdl-Qualified';
import {Type, withSystem} from '@packages/systems/wfdl';

type IconName = BindingContextType['iconName'];

export type ConsolidatedBindingContext = {
  ui: {
    label: string;
    iconName: IconName;
    typeLabel: {
      default: string;
      singular: string;
      plural: string;
    };
  };
  getExpandedBindingSources(): BindingSource[];
  matchesTypeReference(arg1: BindingContextTypeReference): boolean;
  matches(arg1: ConsolidatedBindingContext): boolean;
  matchesExternalKey(arg1: PluginName, arg2: string): boolean;
  getDataType(): Type | null;
};

export default memoize(consolidateBindingContext);

function consolidateBindingContext(
  bindingContext: BindingContext,
  parentBindingContext: BindingContext | null,
  gateways: Gateways
): ConsolidatedBindingContext {
  const {label, type} = bindingContext;
  const gatewayAndBindingContextType = getGatewayAndBindingContextType(
    bindingContext,
    gateways
  );
  const gateway = gatewayAndBindingContextType
    ? gatewayAndBindingContextType.gateway
    : null;
  const bindingContextType = gatewayAndBindingContextType
    ? gatewayAndBindingContextType.bindingContextType
    : null;
  const iconName = getBindingContextIconName(bindingContextType);
  return {
    ui: {
      label,
      iconName,
      typeLabel: getBindingContextTypeLabels(bindingContextType),
    },
    getExpandedBindingSources: once(() => {
      return getExpandedBindingSources(type, gateways);
    }),
    matchesTypeReference(typeReference: BindingContextTypeReference) {
      return areReferencesToSameEntity(bindingContext.type, typeReference);
    },
    matches(other: ConsolidatedBindingContext) {
      return other.matchesTypeReference(bindingContext.type);
    },
    matchesExternalKey(pluginName: PluginName, externalKey: string) {
      return Boolean(
        gateway &&
        pluginNameToString(gateway.pluginName) ===
          pluginNameToString(pluginName) &&
        bindingContextType &&
        bindingContextType.externalKey &&
        bindingContextType.externalKey === externalKey
      );
    },
    getDataType() {
      // We currently only support Dynamo universal bindings in CMS contexts
      const isCMSGateway =
        gateway && pluginNameToString(gateway.pluginName) === 'CMS';

      if (!isCMSGateway || !bindingContextType) {
        return null;
      }

      let parentBindingContextType: BindingContextType | null = null;
      if (parentBindingContext) {
        const parent = getGatewayAndBindingContextType(
          parentBindingContext,
          gateways
        );
        if (parent) {
          // even though Dynamo universal bindings are supported only in CMS
          // contexts, we need to support DYN_CONTEXT conditions for CMS contexts
          // in a Commerce parent context (think Blog Posts collection list on
          // a Product template page)
          const isCmsOrCommerceGateway =
            parent.gateway.pluginName === 'CMS' ||
            parent.gateway.pluginName === 'Commerce';
          const isCollectionEntity =
            parent.bindingContextType.entity === 'Collection';

          if (isCmsOrCommerceGateway && isCollectionEntity) {
            parentBindingContextType = parent.bindingContextType;
          }
        }
      }

      const typeLabels = getBindingContextTypeLabels(bindingContextType);
      return withSystem(
        {label: typeLabels.default},
        getDataType(bindingContextType, parentBindingContextType, gateways)
      );
    },
  };
}

function getGatewayAndBindingContextType(
  bindingContext: BindingContext,
  gateways: Gateways
): {bindingContextType: BindingContextType; gateway: Gateway} | null {
  const {type: typeReference} = bindingContext;
  for (const gateway of gateways) {
    const bindingContextType = gateway.getBindingContextType(typeReference);
    if (bindingContextType) {
      return {bindingContextType, gateway};
    }
  }
  return null;
}

function getBindingContextIconName(type: BindingContextType | null): IconName {
  return type ? type.iconName : 'collection';
}

function getBindingContextTypeLabels(type: BindingContextType | null) {
  if (!type) {
    return {default: '', singular: '', plural: ''};
  }
  return {
    default: type.label,
    singular: type.singularLabel,
    plural: type.pluralLabel,
  };
}

export function forTestSuite_consolidateBindingContext(
  bindingContextId: string,
  gateways: Gateways
): ConsolidatedBindingContext | null {
  const bindingContext = findBindingContext(bindingContextId, gateways);

  if (!bindingContext) {
    return null;
  }

  return consolidateBindingContext(bindingContext, null, gateways);
}

function findBindingContext(
  bindingContextId: string,
  gateways: Gateways
): BindingContext | null {
  const stack: Array<
    PageBindingContext | ListBindingContext | EntityBindingContext
  > = [];

  for (let pluginIndex = 0; pluginIndex < gateways.length; pluginIndex++) {
    const gateway = gateways[pluginIndex];
    // @ts-expect-error - TS18048 - 'gateway' is possibly 'undefined'.
    const rootContext = gateway.getRootBindingContext();

    if (rootContext) {
      // @ts-expect-error - TS18048 - 'gateway' is possibly 'undefined'.
      stack.push(...gateway.getRelativeBindingContexts(rootContext.type));
    }

    // @ts-expect-error - TS18048 - 'gateway' is possibly 'undefined'.
    stack.push(...gateway.getPageBindingContexts());
  }

  const visited = new Set();

  while (stack.length > 0) {
    const bindingContext = stack.pop();
    if (!bindingContext) {
      // TS compiler does not understand stack.length. All it sees is that
      // calling stack.pop() on an empty stack returns `undefined`. We should
      // never reach this block because the stack does not have `undefined`s.
      continue;
    }

    const id = bindingContext.id;
    const typeRef = bindingContext.type;

    if (bindingContextId === id) {
      // found the binding context we are looking for
      return bindingContext;
    }

    if (visited.has(id)) {
      // no need to visit the same binding context twice
      continue;
    }

    visited.add(id);

    // add relative binding contexts to the stack
    for (let pluginIndex = 0; pluginIndex < gateways.length; pluginIndex++) {
      const gateway = gateways[pluginIndex];
      // @ts-expect-error - TS18048 - 'gateway' is possibly 'undefined'.
      const type = gateway.getBindingContextType(typeRef);

      if (type) {
        // @ts-expect-error - TS18048 - 'gateway' is possibly 'undefined'.
        stack.push(...gateway.getRelativeBindingContexts(typeRef));
        break;
      }
    }
  }

  return null;
}
